import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { tryRefresh } from './api';
import Login from './pages/Login';
import Register from './pages/Register';
import SetPassword from './pages/SetPassword';
import Home from './pages/Home';
import { canReadDocuments } from './lib/roles';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import SuperAdmin from './pages/SuperAdmin';
import TenantEdit from './pages/TenantEdit';
// Lazy — html5-qrcode + jspdf sú veľké, načítajú sa len pri mobilnom zbere.
const Capture = React.lazy(() => import('./pages/Capture'));
// Lazy — modul Fakturácia používa len admin s feature flagom.
const Invoices = React.lazy(() => import('./pages/Invoices'));
const Partners = React.lazy(() => import('./pages/Partners'));
// Lazy — modul Objednávky za feature flagom 'orders'.
const Orders = React.lazy(() => import('./pages/Orders'));
// Lazy — modul Účtovníctvo (balík pre účtovníčku) za flagom 'omega_export'.
const Accounting = React.lazy(() => import('./pages/Accounting'));
const Finance = React.lazy(() => import('./pages/Finance'));

function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function App() {
  // Vypršaný JWT ešte neznamená odhlásenie — appka (hlavne PWA na mobile,
  // kde token cez noc expiruje) sa najprv pokúsi o tichú obnovu 30-dňovým
  // refresh tokenom a až keď zlyhá, padá na /login.
  const [booting, setBooting] = useState(() =>
    !isTokenValid(localStorage.getItem('token')) && !!localStorage.getItem('refresh_token'));
  // ?as=<userId> — odkaz z notifikácie patrí inému okruhu tej istej osoby
  // (prepojené účty). Pred vykreslením prepni účet, inak by sa doklad hľadal
  // v nesprávnej firme. Cudzie/neprepojené id server odmietne → ostane pôvodný účet.
  const asUserId = new URLSearchParams(window.location.search).get('as');
  const needsSwitch = (() => {
    if (!asUserId) return false;
    try { return JSON.parse(localStorage.getItem('user') || '{}').id !== asUserId; } catch { return false; }
  })();
  const [switching, setSwitching] = useState(needsSwitch);
  useEffect(() => {
    const stripAs = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('as');
      return url.pathname + url.search;
    };
    const run = async () => {
      if (booting) await tryRefresh();
      if (needsSwitch && asUserId) {
        try {
          const { switchAccount } = await import('./lib/accounts');
          await switchAccount(asUserId);
        } catch { /* neprepojený účet alebo odhlásený — pokračuj pôvodným */ }
        window.location.replace(stripAs());
        return;
      }
      if (asUserId) window.history.replaceState(null, '', stripAs());
      setBooting(false);
      setSwitching(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (booting || switching) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Prihlasujem…</div>;
  }

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!isTokenValid(token)) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  const validToken = isTokenValid(token);
  const isAdmin = user.role === 'admin' || user.isSuperAdmin;
  const isSuperAdmin = user.isSuperAdmin;
  // Rola 'submitter' doklady iba nahráva — na /dashboard nemá čo robiť.
  // Backend to aj tak odmietne (requireDocumentRead), toto je len UX.
  const seesDocuments = canReadDocuments(user);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/set-password" element={<SetPassword />} />
        {/* Home — výber modulu po prihlásení */}
        <Route path="/home" element={validToken ? <Home /> : <Navigate to="/login" />} />
        <Route path="/dashboard" element={validToken ? (seesDocuments ? <Dashboard /> : <Navigate to="/home" />) : <Navigate to="/login" />} />
        {/* /capture si auth rieši sám (mesačný capture token / vlastné prihlásenie). */}
        <Route path="/capture" element={<React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Načítavam…</div>}><Capture /></React.Suspense>} />
        {/* Fakturácia — admin/účtovník + feature flag 'invoicing' (kontroluje stránka aj backend) */}
        <Route path="/invoices" element={validToken && (isAdmin || user.role === 'accountant') ? <React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Načítavam…</div>}><Invoices /></React.Suspense> : <Navigate to="/login" />} />
        <Route path="/partners" element={validToken && (isAdmin || user.role === 'accountant') ? <React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Načítavam…</div>}><Partners /></React.Suspense> : <Navigate to="/login" />} />
        {/* Účtovníctvo — admin/účtovník; feature flag 'omega_export' kontroluje stránka aj backend */}
        <Route path="/accounting" element={validToken && (isAdmin || user.role === 'accountant') ? <React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Načítavam…</div>}><Accounting /></React.Suspense> : <Navigate to="/login" />} />
        {/* Financie — admin/účtovník; feature flag 'finance_overview' kontroluje backend (404) aj Home dlaždica */}
        <Route path="/finance" element={validToken && (isAdmin || user.role === 'accountant') ? <React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Načítavam…</div>}><Finance /></React.Suspense> : <Navigate to="/login" />} />
        {/* Objednávky — ktokoľvek prihlásený; feature flag 'orders' stráži backend (404) aj Home dlaždica */}
        <Route path="/orders" element={validToken ? <React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Načítavam…</div>}><Orders /></React.Suspense> : <Navigate to="/login" />} />
        <Route path="/admin" element={validToken && isAdmin ? <Admin /> : <Navigate to="/login" />} />
        <Route path="/superadmin" element={validToken && isSuperAdmin ? <SuperAdmin /> : <Navigate to="/login" />} />
        <Route path="/superadmin/tenant/:slug" element={validToken && isSuperAdmin ? <TenantEdit /> : <Navigate to="/login" />} />
        <Route path="*" element={<Navigate to={validToken ? "/home" : "/login"} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;